// Safe Refund — one box, any charge type, both Connect flags set by the server.
// Oct 3 2026 (Randy). Replaces the voucher-only tool, whose answer to a tip's
// charge id was "No voucher purchase matches that identifier" — a 404 that read
// like a broken lookup when the real answer was "wrong tool".
//
// The safety is deliberately NOT an extra click: the server re-plans from
// Stripe, caps the amount, claims the operation and keys Stripe's idempotency
// on the same string. So this is one typed REFUND and one button.
import { useEffect, useState } from "react";
import {
  Receipt, MagnifyingGlass, ShieldCheck, Warning, ArrowSquareOut, Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt$ } from "@/lib/financialReview";

const dollars = (cents) => ((cents || 0) / 100).toFixed(2);

function Stat({ label, value, testid, tone }) {
  return (
    <div className="bg-white border hairline rounded px-2 py-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68]">{label}</div>
      <div className={`text-[12px] tabular-nums ${tone || "text-[#1C1B1A]"}`}>{value}</div>
    </div>
  );
}

export default function SafeRefundTool({ prefill, onChanged, dashboardBase }) {
  const [ident, setIdent] = useState("");
  const [plan, setPlan] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ack, setAck] = useState(false);
  const [pullBack, setPullBack] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPlan(null); setConfirm(""); setAck(false); setPullBack(true); };

  const preview = async (value) => {
    const v = (value ?? ident).trim();
    if (!v) { toast.error("Paste the charge, PaymentIntent, checkout session or invoice id."); return; }
    setBusy(true); reset();
    try {
      const r = await api.post("/admin/refunds/preview", { identifier: v });
      setPlan(r.data);
      setAmount(dollars(r.data.max_refundable_cents));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read that payment.", { duration: 9000 });
    } finally { setBusy(false); }
  };

  // "Refund instead of fighting it" on a dispute lands here with the charge.
  // The nonce makes a second click on the SAME charge re-run the preview.
  useEffect(() => {
    if (!prefill?.value) return;
    setIdent(prefill.value);
    preview(prefill.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.value, prefill?.nonce]);

  const issue = async () => {
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!Number.isFinite(cents) || cents <= 0) { toast.error("Enter an amount in dollars."); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/refunds/issue", {
        identifier: ident.trim(), amount_cents: cents, reason: reason.trim(),
        confirm, accept_negative_balance: ack, skip_transfer_reversal: !pullBack,
      });
      toast.success(
        `Refunded ${fmt$(r.data.refunded_cents)}${r.data.creator_pullback_cents
          ? ` and pulled ${fmt$(r.data.creator_pullback_cents)} back from the creator` : ""}.`,
        { duration: 9000 });
      setIdent(""); setReason(""); setAmount(""); reset();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe refused the refund.", { duration: 12000 });
    } finally { setBusy(false); }
  };

  const max = plan?.max_refundable_cents ?? 0;
  const cents = Math.round(parseFloat(amount || "0") * 100);
  const over = plan && cents > max;
  const ready = plan && !plan.blocked && max > 0 && confirm === "REFUND"
    && reason.trim().length >= 4 && !over && cents > 0
    && (plan.needs_ack !== "negative_balance" || ack || !pullBack);

  return (
    <div className="bg-white border hairline rounded-lg overflow-hidden" data-testid="safe-refund-tool">
      <div className="px-4 py-3 border-b hairline bg-[#F7F6F1] flex items-center gap-3">
        <Receipt size={15} weight="duotone" className="text-[#6B6A68] shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-medium text-[#1C1B1A]">Safe refund</div>
          <div className="text-[10px] text-[#6B6A68]">
            Any payment: tips, supporter subscriptions, platform subscriptions, Creator Pro,
            affiliate gate, AI credit. The two Stripe Connect flags are set here, not remembered
            by you.
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <input
            value={ident}
            onChange={(e) => { setIdent(e.target.value); setPlan(null); }}
            onKeyDown={(e) => e.key === "Enter" && preview()}
            placeholder="ch_… · pi_… · cs_… · in_… · or a purchase id"
            data-testid="safe-refund-ident"
            className="flex-1 text-[11px] border hairline rounded px-2 py-1.5 font-mono"
          />
          <button
            type="button" onClick={() => preview()} disabled={busy}
            data-testid="safe-refund-preview"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded border border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360] hover:text-[#1C1B1A] disabled:opacity-50"
          >
            {busy ? <Spinner size={11} className="animate-spin" /> : <MagnifyingGlass size={11} weight="bold" />}
            {busy ? "Reading…" : "Preview"}
          </button>
        </div>

        {plan && (
          <div className="space-y-3" data-testid="safe-refund-plan">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-[#B9B3C9] bg-[#F4F2FF] text-[#4B45CC]"
                    data-testid="safe-refund-flow">
                {plan.flow_label}
              </span>
              {plan.connect && (
                <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-[#B7CBB9] bg-[#F1F7F1] text-[#2F6B3A] inline-flex items-center gap-1"
                      data-testid="safe-refund-connect-tag">
                  <ShieldCheck size={10} weight="bold" /> Connect flags on
                </span>
              )}
              {dashboardBase && plan.payment_intent_id && (
                <a href={`${dashboardBase}/payments/${plan.payment_intent_id}`}
                   target="_blank" rel="noreferrer"
                   data-testid="safe-refund-stripe-link"
                   className="ml-auto text-[10px] text-[#6B6A68] hover:text-[#1C1B1A] inline-flex items-center gap-1">
                  See it in Stripe <ArrowSquareOut size={10} weight="bold" />
                </a>
              )}
            </div>

            <div className="text-[11px] text-[#3A3936] leading-relaxed space-y-1.5" data-testid="safe-refund-story">
              {(plan.story || []).map((line, i) => <p key={i}>{line}</p>)}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Charged" value={fmt$(plan.charged_cents)} testid="safe-refund-charged" />
              <Stat label="Already refunded" value={fmt$(plan.already_refunded_cents)} testid="safe-refund-already" />
              <Stat label="Can still return" value={fmt$(plan.max_refundable_cents)} testid="safe-refund-max"
                    tone="text-[#2F6B3A]" />
              {plan.connect ? (
                <Stat label="Pulled from creator" value={fmt$(plan.creator?.net_received_cents)}
                      testid="safe-refund-pullback" tone="text-[#7A3A2A]" />
              ) : (
                <Stat label="Stripe fee kept" value={fmt$(plan.stripe_fee_cents)} testid="safe-refund-stripefee" />
              )}
            </div>

            {(plan.warnings || []).map((w, i) => (
              <div key={i} className="text-[11px] text-[#8A5A00] bg-[#FDF8EE] border border-[#E0C79A] rounded p-2 flex gap-2"
                   data-testid={`safe-refund-warning-${i}`}>
                <Warning size={13} weight="duotone" className="shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}

            {plan.blocked ? (
              <div className="text-[11px] text-[#7A3A2A] bg-[#FDF1ED] border border-[#E5BBA8] rounded p-2.5"
                   data-testid="safe-refund-blocked">
                {plan.blocked.reason}
                {plan.blocked.where && (
                  <span className="block mt-1 text-[10px] uppercase tracking-[0.16em]">
                    Where to go instead: {plan.blocked.where}
                  </span>
                )}
              </div>
            ) : max > 0 && (
              <div className="border-t hairline pt-3 space-y-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68]">
                    Refund
                  </label>
                  <span className="text-[11px] text-[#6B6A68]">$</span>
                  <input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                    data-testid="safe-refund-amount"
                    className="w-24 text-[12px] border hairline rounded px-2 py-1 tabular-nums"
                  />
                  <button
                    type="button" onClick={() => setAmount(dollars(max))}
                    data-testid="safe-refund-everything"
                    className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360]"
                  >
                    Everything refundable
                  </button>
                  {over && (
                    <span className="text-[11px] text-[#7A3A2A]" data-testid="safe-refund-over">
                      Above what this charge can return.
                    </span>
                  )}
                </div>

                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason — rides on the Stripe refund, the audit trail and the record this closes"
                  data-testid="safe-refund-reason"
                  className="w-full text-[11px] border hairline rounded px-2 py-1.5"
                />

                {plan.connect && (
                  <label className="flex items-start gap-2 text-[11px] text-[#3A3936] cursor-pointer"
                         data-testid="safe-refund-reversal-row">
                    <input
                      type="checkbox" checked={pullBack}
                      onChange={(e) => setPullBack(e.target.checked)}
                      data-testid="safe-refund-reversal-toggle"
                      className="mt-0.5"
                    />
                    <span>
                      Pull the creator's {fmt$(plan.creator?.net_received_cents)} share back
                      (recommended — this is what stops the platform absorbing it).
                      {!pullBack && (
                        <span className="block text-[#7A3A2A] mt-0.5">
                          Off: the platform absorbs {fmt$(plan.creator?.net_received_cents)} and the
                          creator keeps it. The exposure will stay OPEN in the queue as a decision.
                        </span>
                      )}
                    </span>
                  </label>
                )}

                {plan.needs_ack === "negative_balance" && pullBack && (
                  <label className="flex items-start gap-2 text-[11px] text-[#7A3A2A] bg-[#FDF1ED] border border-[#E5BBA8] rounded p-2 cursor-pointer"
                         data-testid="safe-refund-ack-row">
                    <input
                      type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                      data-testid="safe-refund-ack" className="mt-0.5"
                    />
                    <span>
                      I accept that this leaves the creator's Stripe balance negative, which Stripe
                      recovers from their future earnings.
                    </span>
                  </label>
                )}

                <div className="flex items-center gap-2 flex-wrap bg-[#FAF9F5] border hairline rounded p-2.5">
                  <span className="text-[11px] text-[#3A3936]">
                    Type <strong>REFUND</strong> to move {fmt$(cents)} of real money:
                  </span>
                  <input
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value.toUpperCase())}
                    data-testid="safe-refund-confirm"
                    placeholder="REFUND"
                    className="w-24 text-[11px] border hairline rounded px-2 py-1 font-mono tracking-[0.14em]"
                  />
                  <button
                    type="button" onClick={issue} disabled={!ready || busy}
                    data-testid="safe-refund-issue"
                    className="ml-auto text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded bg-[#7A3A2A] text-[#FDFCF8] hover:bg-[#8F4634] disabled:opacity-40"
                  >
                    {busy ? "Refunding…" : `Refund ${fmt$(cents)}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
